import { Component, Input, OnInit } from '@angular/core';
import { TrackerService } from '../../../plugins/tracker/services/tracker.service';
import { TrackeritemRest } from '../../../plugins/tracker/services/trackeritem.rest';
import { ProjectService } from '../../../core/services/project.service';

import * as _ from 'lodash';

@Component({
	selector: 'tracker-query-embeddable',
	template: require('./tracker.query.embeddable.component.html')
})
export class TrackerQueryEmbeddableComponent implements OnInit {
	@Input() url: string;

	project: any = null;
	tracker: any = null;
	trackeritems: any = null;
	loading: boolean = true;
	error: string = '';
	trackerBrowseUrl: string = '';
	trackerBrowseParams: any = {};

	tibOptions: any = {
		showToolbar: false,
		allowDownload: false,
		addModal: false,
		editModal: true,
		showCheckboxes: false
	};

	constructor(
		private trackerService: TrackerService,
		private trackeritemRest: TrackeritemRest,
		private projectService: ProjectService
	) {}

	ngOnInit() {
		if (!this.url) {
			this.error = 'No URL provided';
			this.loading = false;
			return;
		}

		const parsed = this.parseUrl(this.url);
		if (!parsed) {
			this.error = 'Invalid tracker query URL';
			this.loading = false;
			return;
		}

		this.trackerBrowseUrl = this.url;
		this.trackerBrowseParams = { unixName: parsed.unixName, tracker: parsed.trackerId, query: parsed.queryId };
		this.loadData(parsed.unixName, parsed.trackerId, parsed.queryId);
	}

	/**
	 * Parses a GForge tracker browse URL to extract the project, tracker ID and query ID.
	 *
	 * Supported format:
	 *   https://next.gforge.com/#/project/gforge5/tracker/355/browse?query=8224
	 *
	 * @returns An object with unixName, trackerId and queryId, or null if the URL is invalid.
	 */
	parseUrl(url: string): { unixName: string; trackerId: number; queryId: string } | null {
		try {
			// Strip any HTML tags that may have been injected by server-side rendering
			url = url.replace(/<[^>]*>/g, '');

			// Extract the hash fragment (everything after #)
			const hashIndex = url.indexOf('#');
			const fragment = hashIndex >= 0 ? url.substring(hashIndex + 1) : url;

			// Match /project/{unixName}/tracker/{id}/browse
			const trackerMatch = fragment.match(/\/project\/([^/]+)\/tracker\/(\d+)\/browse/);
			if (!trackerMatch) {
				return null;
			}

			const unixName = trackerMatch[1];
			const trackerId = parseInt(trackerMatch[2], 10);

			// Extract query parameter
			const queryParamIndex = fragment.indexOf('?');
			let queryId = '';
			if (queryParamIndex >= 0) {
				const queryString = fragment.substring(queryParamIndex + 1);
				const params = new URLSearchParams(queryString);
				queryId = params.get('query') || '';
			}

			// Sanitize queryId: must be numeric or a custom_<id> token
			if (queryId && !/^(custom_)?\d+$/.test(queryId)) {
				queryId = queryId.replace(/[^\d]/g, '');
			}

			if (!queryId) {
				// Default to custom query for this tracker
				queryId = 'custom_' + trackerId;
			}

			return { unixName, trackerId, queryId };
		} catch (e) {
			return null;
		}
	}

	refresh() {
		const parsed = this.parseUrl(this.url);
		if (parsed) {
			this.trackeritems = null;
			this.fetchTrackeritems(parsed.trackerId, parsed.queryId);
		}
	}

	private loadData(unixName: string, trackerId: number, queryId: string) {
		Promise.all([
			this.trackerService.load(trackerId),
			this.projectService.details(unixName, { rel: 'permissions' })
		]).then(
			([tracker, project]: [any, any]) => {
				this.tracker = tracker;
				this.project = project;
				return this.fetchTrackeritems(trackerId, queryId);
			}
		).catch(
			(err) => {
				console.error('TrackerQueryEmbeddableComponent: failed to load data', err);
				this.error = 'Could not load tracker query results';
				this.loading = false;
			}
		);
	}

	private fetchTrackeritems(trackerId: number, queryId: string): Promise<void> {
		const params: any = { tracker: trackerId, query: queryId };

		// For custom queries, don't send the query param to the API
		if (queryId.toString().match(/(custom)/i)) {
			delete params.query;
		}

		return this.trackeritemRest.fetch(0, params).then(
			(response: any) => {
				this.trackeritems = _.has(response, 'items') && Array.isArray(response.items) ? response.items : [response];
				this.loading = false;
			}
		);
	}
}
